/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef } from 'react';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { useTranslation } from 'react-i18next';
import { RequestDetail, SearchCondition } from 'models/approves/ReferenceOpinion';
import { useNavigate } from 'react-router-dom';
import ApproveRequestModal from '../../approves/ApproveRequestModal';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { ComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { ApproveRequestPageType } from '../../approves/ApproveRequestDetailCase';
import { findElmRolesRequest } from 'apis/admin/Role';
import CustomGrid from 'components/grids/CustomGrid';
import { CommonUtil } from 'utils/CommonUtil';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import useSessionStore from 'stores/useSessionStore';
import { useMessageBar } from 'hooks/useMessageBar';
import { findApproveRequestDetail } from 'apis/approves/approves';
import { EVENT_TYPES, EventBus } from 'utils/EventUtil';

export interface values {
  pageId: string;
  pageIdx: string;
}

type Props = {
  changeAprTpl?: (aprTplId: string) => void;
};

const AuthRequestListPage = (props: Props) => {
  const gridRef = useRef<any>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userId } = useSessionStore();
  const { openMessageBar } = useMessageBar();
  const [rowData, setRowData] = useState<RequestDetail[]>([]);
  const [condition, setCondition] = useState<SearchCondition>({});
  const [isOpenRequestModal, setOpenRequestModal] = useState<boolean>(false);
  const [aprReqProgStatCd, setAprReqProgStatCd] = useState<any>();

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      minWidth: 40,
      width: 40,
      align: 'center',
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'aprReqTitNm',
      header: String(t('com.grid.요청제목', '요청제목')),
      align: 'left',
      minWidth: 100,
      width: '*',
      cellTemplate: CellMaker.makeLink({
        click: (e, ctx) => {
          moveToPage(ctx.item);
        },
      }),
    },
    {
      binding: 'dataInsUserId',
      visible: false,
    },
    {
      binding: 'aprReqProgStatNm',
      header: String(t('com.label.결재상태', '결재상태')),
      minWidth: 100,
      width: 100,
      cssClass: 'WijmoTag',
      cellTemplate: (item) => {
        if (item.item.aprReqProgStatCd === 'APV') {
          return `<span class="grey">${item.value}</span>`;
        } else if (item.item.aprReqProgStatCd === 'CCL') {
          return `<span class="purple">${item.value}</span>`;
        } else if (item.item.aprReqProgStatCd === 'INP') {
          return `<span class="yellow">${item.value}</span>`;
        } else if (item.item.aprReqProgStatCd === 'REJ') {
          return `<span class="red">${item.value}</span>`;
        } else if (item.item.aprReqProgStatCd === 'REQ') {
          return `<span class="green">${item.value}</span>`;
        } else if (item.item.aprReqProgStatCd === 'TMP') {
          return `<span class="blue">${item.value}</span>`;
        }
      },
    },
    {
      binding: 'aprReqUserNm',
      header: String(t('com.grid.요청자', '요청자')),
      align: 'left',
      minWidth: 150,
      width: 300,
    },

    {
      binding: 'dataInsDtm',
      header: String(t('com.label.요청일', '요청일')),
      align: 'center',
      minWidth: 80,
      width: 160,
    },
    {
      binding: 'aprAprvUserNm',
      header: String(t('com.label.결재자', '결재자')),
      align: 'left',
      minWidth: 150,
      width: 300,
    },
    {
      binding: 'aprAprvDtm',
      header: String(t('com.label.결재완료일시', '결재완료일시')),
      align: 'center',
      minWidth: 80,
      width: 160,
    },
  ];

  const handleSearch = async (params) => {
    const result = await findElmRolesRequest(params);
    if (result) {
      setRowData(result);
    }
  };

  useEffect(() => {
    getCommonCodes();
    handleSearch(condition);

    const unsubscribe = EventBus.subscribe(EVENT_TYPES.ELM_AUTH_REQ_LIST, () => {
      handleSearch(condition);
    });
    return unsubscribe;
  }, []);

  const moveToPage = (item: any) => {
    if (item.aprReqId) {
      // 목록 재조회가 안된 경우 화면 분기를 위해 재조회
      findApproveRequestDetail(item.aprReqId).then((result) => {
        if (!result) {
          openMessageBar({
            type: 'error',
            content: t('com.msg.결재 정보가 없습니다.', '결재 정보가 없습니다.'),
          });
          handleSearch(condition);
          return;
        }
        // 임시저장인 경우 작성자만 접근 가능
        if (result.aprReqProgStatCd == 'TMP') {
          if (result.aprReqUserId === userId) {
            navigate('/approves/approve-request-page?aprReqId=' + result.aprReqId, {
              state: {
                upprMnuUrl: location.pathname,
                mnuId: `/approves/approve-request-page`,
                mnuNm: t('asst.label.결재현황', '결재현황'),
              },
            });
          } else {
            openMessageBar({
              type: 'warning',
              content:
                `${result?.aprReqUserNm}` +
                String(t('com.msg.님이 작성중입니다.', '님이 작성중입니다.')),
            });
          }
        } else {
          navigate('/approves/approve-request-detail?aprReqId=' + result.aprReqId, {
            state: {
              upprMnuUrl: location.pathname,
              mnuId: `/approves/approve-request-detail`,
              mnuNm: t('asst.label.결재현황', '결재현황'),
            },
          });
        }
      });
    }
  };

  const openRequestModal = () => {
    /*setRequestModalCondition({
      pageId: ApproveRequestPageType.EXCP_AUTH_REQ,
      mode: PageMode.WRITE,
      excpId: userSession?.deptCd,
    });*/
    setOpenRequestModal(true);
  };

  const getCommonCodes = async () => {
    const aprReqProgStatCd = await getCommonCodeNames('APR_REQ_PROG_STAT_CD');
    setAprReqProgStatCd(aprReqProgStatCd);
  };

  const handleOnChange = (name, value) => {
    setCondition((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(condition);
    }
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('com.label.ELM권한요청목록', 'ELM권한요청목록'),
      t('com.label.결재현황', '결재현황')
    );
  };

  return (
    <ContainerLayout>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('com.label.결재상태', '결재상태')}</label>
                <ComboBox
                  options={aprReqProgStatCd}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.aprReqProgStatCd}
                  onChange={(value) => {
                    handleOnChange('aprReqProgStatCd', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.요청제목', '요청제목')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="aprReqTitNm"
                  placeholder={String(
                    t('com.label.요청명을 입력해 주세요.', '요청명을 입력해 주세요.')
                  )}
                  value={condition?.aprReqTitNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.요청자', '요청자')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="dataInsUserId"
                  placeholder={String(
                    t('com.label.작성자를 입력해주세요.', '작성자를 입력해주세요.')
                  )}
                  value={condition?.dataInsUserId}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons setReset={setCondition} onSearch={() => handleSearch(condition)} />
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('com.label.결재현황', '결재현황')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={550}
        isSelector={false}
        isReadOnly={true}
        initialized={onInitialized}
      />
      <GlobalBtnGroup>
        <Button css={IconButton.button} className="create" onClick={openRequestModal} disableRipple>
          {t('com.button.ELM권한요청', 'ELM권한요청')}
        </Button>
      </GlobalBtnGroup>

      {isOpenRequestModal && (
        <ApproveRequestModal
          open={isOpenRequestModal}
          close={() => {
            handleSearch(condition);
            setOpenRequestModal(false);
          }}
          pageId={ApproveRequestPageType.ELM_AUTH_REQ}
          condition={{
            aprPageId: ApproveRequestPageType.ELM_AUTH_REQ,
            optValCtn1: ApproveRequestPageType.ELM_AUTH_REQ,
          }}
        />
      )}
    </ContainerLayout>
  );
};

export default AuthRequestListPage;
